/**
 * Data Grid Component
 *
 * A reusable data grid component for displaying tabular data in admin pages.
 * Supports sorting, custom cell renderers, and responsive design.
 *
 * Features:
 * - Sortable columns
 * - Custom cell rendering
 * - Empty state handling
 * - Loading state
 * - Responsive horizontal scrolling
 */

import { useState, useCallback, useMemo } from 'react';

/**
 * Column definition for the data grid
 */
export interface DataGridColumn<T> {
  /** Unique key for the column */
  key: string;
  /** Header label */
  label: string;
  /** Accessor function to get the cell value */
  accessor?: (row: T) => unknown;
  /** Custom cell renderer */
  render?: (value: unknown, row: T) => React.ReactNode;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Column width (CSS value) */
  width?: string;
  /** Minimum column width (CSS value) */
  minWidth?: string;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
}

/**
 * Sort direction
 */
export type SortDirection = 'asc' | 'desc' | null;

/**
 * Sort state
 */
export interface SortState {
  column: string | null;
  direction: SortDirection;
}

/**
 * Props for the DataGrid component
 */
interface DataGridProps<T> {
  /** Column definitions */
  columns: DataGridColumn<T>[];
  /** Data rows */
  data: T[];
  /** Function to get unique key for each row */
  getRowKey: (row: T) => string;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Initial sort state */
  initialSort?: SortState;
  /** Callback when sort changes */
  onSortChange?: (sort: SortState) => void;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Whether rows are clickable */
  clickableRows?: boolean;
  /** Maximum height for the grid (enables vertical scrolling) */
  maxHeight?: string;
}

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Sort indicator icon
 */
function SortIcon({ direction }: { direction: SortDirection }) {
  if (direction === null) {
    return (
      <svg className="w-4 h-4 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  if (direction === 'asc') {
    return (
      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );
  }

  return (
    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/**
 * Empty state component
 */
function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 mb-4">
        <svg className="w-8 h-8 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
      <p className="text-white/60">{message}</p>
    </div>
  );
}

/**
 * Get default value for a cell
 */
function getDefaultValue<T>(row: T, key: string): unknown {
  const value = (row as Record<string, unknown>)[key];
  return value;
}

/**
 * Format cell value for display
 */
function formatCellValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-white/30">-</span>;
  }

  if (typeof value === 'boolean') {
    return value ? (
      <span className="text-green-400">Yes</span>
    ) : (
      <span className="text-white/40">No</span>
    );
  }

  if (value instanceof Date) {
    return value.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  if (Array.isArray(value)) {
    return value.join(', ') || <span className="text-white/30">-</span>;
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Compare function for sorting
 */
function compareValues(a: unknown, b: unknown, direction: SortDirection): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  let result = 0;

  if (typeof a === 'string' && typeof b === 'string') {
    result = a.localeCompare(b);
  } else if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else if (a instanceof Date && b instanceof Date) {
    result = a.getTime() - b.getTime();
  } else {
    result = String(a).localeCompare(String(b));
  }

  return direction === 'desc' ? -result : result;
}

/**
 * DataGrid component
 */
export function DataGrid<T>({
  columns,
  data,
  getRowKey,
  isLoading = false,
  emptyMessage = 'No data available',
  initialSort = { column: null, direction: null },
  onSortChange,
  onRowClick,
  clickableRows = false,
  maxHeight,
}: DataGridProps<T>) {
  const [sortState, setSortState] = useState<SortState>(initialSort);

  /**
   * Handle column header click for sorting
   */
  const handleSort = useCallback((column: DataGridColumn<T>) => {
    if (!column.sortable) return;

    setSortState((prev) => {
      let newDirection: SortDirection;

      if (prev.column !== column.key) {
        newDirection = 'asc';
      } else if (prev.direction === 'asc') {
        newDirection = 'desc';
      } else if (prev.direction === 'desc') {
        newDirection = null;
      } else {
        newDirection = 'asc';
      }

      const newState: SortState = {
        column: newDirection ? column.key : null,
        direction: newDirection,
      };

      onSortChange?.(newState);
      return newState;
    });
  }, [onSortChange]);

  /**
   * Sort data based on current sort state
   */
  const sortedData = useMemo(() => {
    if (!sortState.column || !sortState.direction) {
      return data;
    }

    const column = columns.find((c) => c.key === sortState.column);
    if (!column) return data;

    return [...data].sort((a, b) => {
      const aValue = column.accessor ? column.accessor(a) : getDefaultValue(a, column.key);
      const bValue = column.accessor ? column.accessor(b) : getDefaultValue(b, column.key);
      return compareValues(aValue, bValue, sortState.direction);
    });
  }, [data, columns, sortState]);

  /**
   * Get cell value for a column
   */
  const getCellValue = useCallback((row: T, column: DataGridColumn<T>): unknown => {
    if (column.accessor) {
      return column.accessor(row);
    }
    return getDefaultValue(row, column.key);
  }, []);

  /**
   * Render cell content
   */
  const renderCell = useCallback((row: T, column: DataGridColumn<T>): React.ReactNode => {
    const value = getCellValue(row, column);

    if (column.render) {
      return column.render(value, row);
    }

    return formatCellValue(value);
  }, [getCellValue]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return <EmptyState message={emptyMessage} />;
  }

  return (
    <div
      className="overflow-auto border border-white/10 rounded-lg"
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="w-full border-collapse">
        <thead className="sticky top-0 bg-secondary-dark z-10">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-4 py-3 text-left text-sm font-medium text-white/80 border-b border-white/10 ${
                  column.sortable ? 'cursor-pointer hover:text-white hover:bg-white/5 select-none' : ''
                }`}
                style={{
                  width: column.width,
                  minWidth: column.minWidth || '100px',
                  textAlign: column.align || 'left',
                }}
                onClick={column.sortable ? () => handleSort(column) : undefined}
              >
                <div className={`flex items-center gap-2 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}>
                  <span>{column.label}</span>
                  {column.sortable && (
                    <SortIcon
                      direction={sortState.column === column.key ? sortState.direction : null}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr
              key={getRowKey(row)}
              className={`border-b border-white/5 last:border-b-0 ${
                clickableRows || onRowClick
                  ? 'cursor-pointer hover:bg-white/5 transition-colors'
                  : ''
              }`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className="px-4 py-3 text-sm text-white/80"
                  style={{
                    textAlign: column.align || 'left',
                  }}
                >
                  {renderCell(row, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataGrid;
